import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { LegalIntro, LegalPage, LegalSection, legalLinkClass } from "../components/legal/LegalPage";
import { emitOpenConsentPreferences } from "../utils/consent";

// Ported from forzabuilt-website's src/pages/do-not-sell-or-share.astro.
const DoNotSellOrShare = () => (
  <LegalPage title="Do Not Sell or Share My Personal Information" effectiveDate="October 16, 2026">
    <Helmet>
      <title>Do Not Sell or Share My Personal Information | RuggedRed</title>
      <meta
        name="description"
        content="Submit a request to opt out of uses and disclosures that may be considered a sale or sharing of personal information under applicable U.S. state laws."
      />
      <link rel="canonical" href="https://www.ruggedred.com/do-not-sell-or-share" />
    </Helmet>

    <LegalIntro>
      <p>
        Residents of certain U.S. states may request to opt out of uses or disclosures of personal
        information that may be considered a "sale" or "sharing" under applicable law.
      </p>
      <p>You can use any of the options below to submit your request.</p>
    </LegalIntro>

    <LegalSection title="What This Right Covers">
      <p>
        Under the California Consumer Privacy Act (CCPA), as amended, and similar laws in other
        states, "personal information" broadly covers information that identifies, relates to, or
        could reasonably be linked with a particular consumer or household. Depending on how you
        interact with our website, this can include identifiers such as your name and contact
        details, and technical or usage information such as browsing activity, device data, and
        analytics or advertising identifiers collected through cookies and similar technologies
        described in our{" "}
        <Link to="/cookie-policy" className={legalLinkClass}>
          Cookie Policy
        </Link>
        .
      </p>
      <p>
        Opting out through this page applies specifically to activity that may be considered a
        "sale" or "sharing" of personal information under applicable law, such as certain uses of
        advertising and analytics cookies. It does not delete your personal information, unsubscribe
        you from communications you've separately opted into, or affect our ability to provide the
        products, support, or services you've requested from us. If you would like to request
        deletion, correction, or a copy of your personal information, email us at{" "}
        <a href="mailto:info@ruggedred.com" className={legalLinkClass}>
          info@ruggedred.com
        </a>
        .
      </p>
    </LegalSection>

    <LegalSection title="Request Options">
      <ul className="space-y-3 list-disc pl-6">
        <li>
          Email us at{" "}
          <a href="mailto:info@ruggedred.com" className={legalLinkClass}>
            info@ruggedred.com
          </a>
        </li>
        <li>
          Update tracking preferences at any time with the "Cookie Preferences" link in the website
          footer, or{" "}
          <button
            type="button"
            onClick={() => emitOpenConsentPreferences("do-not-sell")}
            className={legalLinkClass}
          >
            opt out now
          </button>{" "}
          (opens Cookie Preferences with non-essential cookies switched off; click Save
          Preferences to confirm).
        </li>
        <li>
          Use Global Privacy Control (GPC) in a supporting browser, as described in our{" "}
          <Link to="/cookie-policy" className={legalLinkClass}>
            Cookie Policy
          </Link>
          .
        </li>
      </ul>
    </LegalSection>

    <LegalSection title="Verification and Response">
      <p>
        To protect your information, we may need to verify your identity before completing your
        request. We will respond within the timeframe required by applicable law. Under the CCPA,
        that generally means acknowledging your request within 10 business days and responding
        substantively within 45 calendar days of receipt, with a further 45-day extension available
        for complex requests where notice of the extension and the reason for it is provided to you.
      </p>
      <p>
        You may also authorize an agent to submit a request on your behalf, subject to verification
        and authorization requirements. We may ask an authorized agent to provide proof that you
        granted them permission to act on your behalf, and we may still require you to verify your
        own identity directly with us or confirm the agent's authority before the request is
        completed.
      </p>
    </LegalSection>

    <LegalSection title="After You Submit a Request">
      <p>
        Once an opt-out request is verified and processed, we stop the specific uses and disclosures
        of your personal information that qualify as a "sale" or "sharing" under applicable law
        going forward. This is not a one-time action limited to the moment you submit the request.
        It remains in effect unless you later choose to change your cookie preferences or otherwise
        opt back in.
      </p>
      <p>
        Exercising this right does not affect your ability to use our website, purchase our
        products, or receive customer and technical support. In accordance with the CCPA, we do not
        charge different prices, provide a different level of service, or otherwise discriminate
        against you for submitting a request under this policy.
      </p>
    </LegalSection>

    <LegalSection title="Related Policies">
      <p>
        For more information, see our{" "}
        <Link to="/cookie-policy" className={legalLinkClass}>
          Cookie Policy
        </Link>
        .
      </p>
    </LegalSection>
  </LegalPage>
);

export default DoNotSellOrShare;
